// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddUserTravelDocumentDialog from './AddUserTravelDocumentDialog';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../../BackdropProgress.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CustomSnackbar from '../../ui/Snackbars';
import EnhancedTableHead from '../../ui/EnhancedTableHead.js';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import SearchIcon from "@mui/icons-material/Search";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import useHttp from '../../../utils/http';
import UserTravelDocumentInfoCard from './UserTravelDocumentInfoCard';
import wso2Theme from '../../../theme';
import { adjustForTimezone, getCountryNameFromCountryCode, getDateForDB, getCountryList, getTimeLabelAndColor, truncateString, getComparator, stableSort  } from '../../../utils/formatting';
import { isAccessGranted } from "../../../utils/oauth";
import { postSearchOwnTravelDocuments } from '../../../utils/end-points.js';
import { useEffect, useState, useCallback } from 'react';
import { travelDocumentStatus, travelDocumentStatusArray, travelDocumentTypesArray } from '../../../utils/const';
import { PRIVILEGES } from "../../../Config.js";
import { getTravelDocumentStatusColor } from "../../../theme/status-colors"
import "../../../App.css";
import "../../../css/UserTravelDocumentList.scss"

const headCells = [
    { id: 'document_no', label: 'Document Number', align: 'left', disablePadding: false, sortable: true },
    { id: 'document_type', label: 'Document Type', align: 'left', disablePadding: false, sortable: true },
    { id: 'issued_country', label: 'Issued Country', align: 'left', disablePadding: false, sortable: true },
    { id: 'issued_date', label: 'Issued Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'expiry_date', label: 'Expiry Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'daysToExpire', label: 'Expires In', align: 'center', disablePadding: false, sortable: false },
];

const UserTravelDocumentList = () => {

    const { handleRequest } = useHttp()
    const [filtersExpanded, setFiltersExpanded] = useState(false)
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('expiry_date')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(8)
    const [documentList, setDocumentList] = useState([])
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [isPageLoading, setPageLoading] = useState(true)
    const [isTableLoading, setTableLoading] = useState(false)
    const [isTablePaginating, setTablePaginating] = useState(false)
    const [addDocumentDialog, setAddDocumentDialog] = useState(false)
    const [countryFilter, setCountryFilter] = useState(null)
    const [documentTypeFilter, setDocumentTypeFilter] = useState(null)
    const [statusFilter, setStatusFilter] = useState("Active")
    const [searchFilter, setSearchFilter] = useState("")
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })

    useEffect(() => {
        fetchTravelDocumentList(rowsPerPage, "", null, null, "Active", "expiry_date", "asc")
        if (window.innerHeight >= 850) {
            setRowsPerPage(12)
        }

    }, [])

    useEffect(() => {
        fetchTravelDocumentList(rowsPerPage, searchFilter, countryFilter, documentTypeFilter, statusFilter, orderBy, order)
    }, [countryFilter, documentTypeFilter, orderBy, order, statusFilter])

    const fetchTravelDocumentList = useCallback(async (limit, searchFilter, country, documentType, status, sortColumn, sortOrder, showSuccessSnack) => {
        setTableLoading(true)
        let body = {
            status: "active",
            limit: limit,
            offset: 0,
            searchString: searchFilter,
            country: country?.countryCode,
            documentType: documentType,
            minExpiryDate: status === "Active" ? new Date().toISOString().slice(0, 10) : null,
            maxExpiryDate: status === "Expired" ? new Date().toISOString().slice(0, 10) : null,
            sortColumn: sortColumn,
            sortOrder: sortOrder
        }

        handleRequest(postSearchOwnTravelDocuments, "POST", body, (travelDocuments) => {
            if (travelDocuments) {
                setDocumentList(travelDocuments)
                if (travelDocuments.length > 0) {
                    setSelectedDocument(travelDocuments[0])
                }
                else {
                    setSelectedDocument(null)
                }
                if (showSuccessSnack) {
                    setSnackbarData({ message: "Success", open: true, severity: "success", onClose: onCloseSnackbar })
                }
            }
            else {
                setSnackbarData({ message: "Error while fetching Travel Documents", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setTableLoading(false)
            setPageLoading(false)
        }, (error) => {
            setTableLoading(false)
            setPageLoading(false)
            setSnackbarData({ message: "Error while fetching Travel Documents", open: true, severity: "error", onClose: onCloseSnackbar })
            console.error(error)
        })

    }, [])

    const loadMoreTravelDocuments = (limit, offset, searchFilter, country, documentType, status, sortColumn, sortOrder) => {
        setTablePaginating(true)
        let body = {
            status: "active",
            limit: limit,
            offset: offset,
            searchString: searchFilter,
            country: country?.countryCode,
            documentType: documentType,
            minExpiryDate: status === "Active" ? new Date().toISOString().slice(0, 10) : null,
            maxExpiryDate: status === "Expired" ? new Date().toISOString().slice(0, 10) : null,
            sortColumn: sortColumn,
            sortOrder: sortOrder
        }

        handleRequest(postSearchOwnTravelDocuments, "POST", body, (travelDocuments) => {
            if (travelDocuments) {
                let tempDocumentList = documentList.concat(travelDocuments)
                setDocumentList(tempDocumentList)
                if (travelDocuments.length === 0) {
                    setPage(page => page - 1)
                    setSnackbarData({ message: "No more data", open: true, severity: "success", onClose: onCloseSnackbar })
                }
                setTablePaginating(false)
            }
            else {
                setSnackbarData({ message: "Error while fetching visas", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setPageLoading(false)
        }, (error) => {
            console.error(error)
            setPageLoading(false)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        fetchTravelDocumentList(parseInt(event.target.value, 10), searchFilter, countryFilter, documentTypeFilter, statusFilter, orderBy, order)
        setPage(0);
    }

    const handleChangePage = (event, newPage) => {
        if (documentList.length <= newPage * rowsPerPage) {
            loadMoreTravelDocuments(rowsPerPage, documentList.length, searchFilter, countryFilter, documentTypeFilter, statusFilter, orderBy, order)
        }
        setPage(newPage);
    }

    const handleClick = (document) => {
        setSelectedDocument(document)
    }

    const handleCountryFilter = (event, value) => {
        setCountryFilter(value)
    }

    const handleDocumentTypeFilter = (event, value) => {
        setDocumentTypeFilter(value)
    }

    const handleStatusFilter = (event, value) => {
        setStatusFilter(value)
    }

    const handleSearchFilter = (event) => {
        setSearchFilter(event.target.value)
        if (!event.target.value) {
            fetchTravelDocumentList(rowsPerPage, event.target.value, countryFilter, documentTypeFilter, statusFilter, orderBy, order)
        }
    }

    const searchTable = () => {
        fetchTravelDocumentList(rowsPerPage, searchFilter, countryFilter, documentTypeFilter, statusFilter, orderBy, order)
    }

    const getTimeToExpire = (endDate) => {

        var documentEndDate = adjustForTimezone(new Date(endDate))
        var currentDate = new Date()

        if (!endDate) {
            return <span className='Status-chip'
                style={{
                    backgroundColor: wso2Theme.palette.status.completed,
                    color: "white"
                }}>
                No Expire
            </span>
        }
        // TODO : make more readable
        return <span className='Status-chip'
            style={{
                backgroundColor: (getDateForDB(currentDate) <= getDateForDB(documentEndDate)) ?
                    getTimeLabelAndColor(documentEndDate, currentDate)[1] :
                    wso2Theme.palette.status.expired,
                color: wso2Theme.palette.colors.white
            }}>
            {(getDateForDB(currentDate) <= getDateForDB(documentEndDate)) ? getTimeLabelAndColor(documentEndDate, currentDate)[0] : "Expired"}
        </span>
    }

    const handleCloseTravelDocumentDialog = () => {
        setAddDocumentDialog(false)
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (
        <div className='User-travel-document-list'>
            <CustomSnackbar snackbar={snackbarData} />

            <div className='Page-title'>My Travel Documents</div>

            <BackdropProgress open={isPageLoading} />

            {!isPageLoading && (
                <div>
                    <AddUserTravelDocumentDialog open={addDocumentDialog} onClose={handleCloseTravelDocumentDialog} refreshTravelDocumentList={() => fetchTravelDocumentList(rowsPerPage, searchFilter, countryFilter, documentTypeFilter, statusFilter, orderBy, order, true)} documentList={documentList} />

                    <Grid container>
                        <Grid item lg={8} md={7} xs={12} className='Table-grid'>

                            <Paper className='Border-elevation'>
                                <div className='Table-actions-div' style={{ borderColor: wso2Theme.palette.primary.light }}>
                                    <div className="Table-actions">

                                        <TextField variant="outlined" label="Search" size="small"
                                            className='Search-text-field'
                                            onChange={handleSearchFilter} />
                                        <Button variant={"contained"} startIcon={<SearchIcon />} color="secondary" className='Filter-button'
                                            onClick={searchTable} >Search</Button>
                                        <Button variant={filtersExpanded ? "contained" : "outlined"} startIcon={<FilterAltIcon />} color="primary" className='Filter-button'
                                            onClick={() => setFiltersExpanded(!filtersExpanded)} >{filtersExpanded ? "Hide Filters" : "Show Filters"}</Button>

                                        {isAccessGranted([PRIVILEGES.ADD_OWN_TRAVEL_DOCUMENT]) && <Button variant={"contained"} startIcon={<AddCircleOutlineIcon />} color="secondary" className='Add-document-button'
                                            onClick={() => setAddDocumentDialog(true)} >Add Document</Button>}

                                    </div>
                                    {filtersExpanded && (
                                        <div className='Filters-div'>
                                            <Grid container>
                                                <Grid item lg={4} md={6} className='Filter-grid-item-country'>
                                                    <Autocomplete
                                                        size="small"
                                                        noOptionsText="No options"
                                                        disablePortal
                                                        value={countryFilter}
                                                        options={getCountryList()}
                                                        getOptionLabel={(option) => option.country}
                                                        onChange={handleCountryFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props}>{option.country}</Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Country" />}
                                                    />
                                                </Grid>
                                                <Grid item lg={4} md={6} className='Filter-grid-item-document-type'>
                                                    <Autocomplete
                                                        size="small"
                                                        noOptionsText='No options'
                                                        disablePortal
                                                        value={documentTypeFilter}
                                                        options={travelDocumentTypesArray}
                                                        getOptionLabel={(option) => option}
                                                        onChange={handleDocumentTypeFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props}>{option}</Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Document Type" />}
                                                    />
                                                </Grid>
                                                <Grid item lg={4} md={6} className='Filter-grid-item-status'>
                                                    <Autocomplete
                                                        size="small"
                                                        noOptionsText='No options'
                                                        disablePortal
                                                        options={travelDocumentStatusArray.filter(status => status !== travelDocumentStatus.INACTIVE)}
                                                        getOptionLabel={(option) => option}
                                                        defaultValue={statusFilter}
                                                        onChange={handleStatusFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props} >
                                                                <span className="Status-dot-dropdown" style={{ backgroundColor: (option == travelDocumentStatus.ACTIVE) ? getTravelDocumentStatusColor(true) : getTravelDocumentStatusColor(false) }}></span>
                                                                {option}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Status" />}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </div>
                                    )}
                                </div>
                                {isTableLoading && <LinearProgress color='secondary' />}

                                <TableContainer>
                                    <Table size={'small'} >
                                        <EnhancedTableHead headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                        <TableBody>
                                            {selectedDocument && stableSort(documentList, getComparator(order, orderBy))
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row) => {
                                                    return (
                                                        <TableRow onClick={(event) => handleClick(row)} tabIndex={-1} key={row.id}
                                                            className={selectedDocument.id === row.id ? 'Selected-row' : "Table-row"}
                                                            style={{
                                                                backgroundColor: selectedDocument.id === row.id ? "white" : null,
                                                                elevation: selectedDocument.id === row.id ? "above" : null,
                                                            }}>
                                                            <TableCell className='Table-cell'>{truncateString(row.documentNo, 20)}</TableCell>
                                                            <TableCell className='Table-cell'>{truncateString(row.documentType, 20)}</TableCell>
                                                            <TableCell className='Table-cell'>{truncateString(getCountryNameFromCountryCode(row.issuedCountry) ? getCountryNameFromCountryCode(row.issuedCountry) : row.issuedCountry, 20)}</TableCell>
                                                            <TableCell className='Table-cell'>{getDateForDB(adjustForTimezone(new Date(row.issuedDate)))}</TableCell>
                                                            <TableCell className='Table-cell'>{row.expiryDate ? getDateForDB(adjustForTimezone(new Date(row.expiryDate))) : "None"}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-days'>{getTimeToExpire(row.expiryDate)}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                    {!isPageLoading && documentList.length === 0 && <div className='No-documents-label'>No Travel Documents</div>}
                                </TableContainer>
                                {isTablePaginating && <LinearProgress color='secondary' />}
                                <TablePagination
                                    rowsPerPageOptions={window.innerHeight >= 850 ? [5, 12, 25] : [5, 8, 25]}
                                    component="div"
                                    count={-1}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Paper>
                        </Grid>{selectedDocument ?
                            <Grid item lg={4} md={5} xs={12} className='Details-grid'>
                                <UserTravelDocumentInfoCard selectedDocument={selectedDocument} refreshTravelDocumentList={() => fetchTravelDocumentList(rowsPerPage, searchFilter, countryFilter, documentTypeFilter, statusFilter, orderBy, order, true)} />
                            </Grid> : null}
                    </Grid>
                </div>
            )}

        </div>
    )

}

export default UserTravelDocumentList;